import { Box, getTokenCssVar, H2, Li, Link, Ul } from '@vp/swan'
import type { NavigationCategory, NavigationItem } from '../../../../types/navigation'
import { getHeaderTracker } from '../../../../tracking'
import { CATEGORY_SECTION, MERCHANDISING_SECTION, TEXT_LAYOUT_LINK, TOP_NAV } from '../../../../constants/analytics'
import { memo, useMemo } from 'react'
import { MAX_THIRD_LEVEL_ITEMS } from '../../../../constants/config'
import BadgeWithText from '../../BadgeWithText'
import { useNavContext } from '../../../../context/NavContextProvider'
import { useHoverIntent } from '../../../../hooks/useHoverIntent'
import { ComponentType } from '../../../../types/components-map'

/** Tracks a navigation link click for analytics */
export const trackNavigationClick = (navigationDetailPath: string[], item: NavigationItem, isMxNav): void => {
  getHeaderTracker().trackLink({
    subSection: TOP_NAV,
    navigationDetailPath,
    navigationSection: isMxNav ? (item.inMx ? MERCHANDISING_SECTION : CATEGORY_SECTION) : undefined,
    href: item.url,
    linkType: TEXT_LAYOUT_LINK,
  })
}

interface SubCategoryProps {
  readonly navItem: NavigationItem
  readonly parent: NavigationCategory
}

/**
 * A subcategory within the flyout: renders the category header and its child links.
 */
export const SubCategory: React.FC<SubCategoryProps> = memo(({ navItem, parent }) => {
  const { hoverSubNavItem, isMxNav } = useNavContext()
  const subCategoryRef = useHoverIntent<HTMLLIElement>({
    hoverIn: () => hoverSubNavItem(parent.id, navItem.id),
  })
  const itemChildren = useMemo(() => (navItem.children ?? []).slice(0, MAX_THIRD_LEVEL_ITEMS), [navItem.children])

  const handleTracking = (child?: NavigationItem) => () => {
    if (child) {
      trackNavigationClick([parent.analyticsId, navItem.analyticsId, child.analyticsId], child, isMxNav)
    } else {
      trackNavigationClick([parent.analyticsId, navItem.analyticsId], navItem, isMxNav)
    }
  }

  const handleKeyDown = (child?: NavigationItem) => (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (['Enter', ' '].includes(e.key)) {
      handleTracking(child)()
    }
  }

  return (
    <Li ref={subCategoryRef} onFocus={() => hoverSubNavItem(parent.id, navItem.id)}>
      <Box marginBottom={2}>
        {navItem.url ? (
          <Link
            skin="unstyled"
            href={navItem.url}
            onClick={handleTracking()}
            onKeyDown={handleKeyDown()}
            data-position={navItem.itemPosition}
            data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
            data-translation={[parent.analyticsId, navItem.analyticsId].join('|')}
          >
            <H2 fontSkin="body-standard-bold" style={{ margin: 0 }}>
              <BadgeWithText badge={navItem.badge} text={navItem.text} />
            </H2>
          </Link>
        ) : (
          <H2 fontSkin="body-standard-bold" style={{ margin: 0, color: getTokenCssVar('SwanSemColorFgSubtle') }}>
            <BadgeWithText badge={navItem.badge} text={navItem.text} />
          </H2>
        )}
      </Box>

      {itemChildren.length > 0 && (
        <Ul skin="minimal">
          {itemChildren.map((child) => (
            <Li key={child.id}>
              <Link
                skin="unstyled"
                fontSkin="body-small"
                href={child.url}
                py={1}
                onClick={handleTracking(child)}
                onKeyDown={handleKeyDown(child)}
                data-position={child.itemPosition}
                data-section={`${ComponentType.HEADER}:${TOP_NAV}`}
                data-translation={[parent.analyticsId, navItem.analyticsId, child.analyticsId].join('|')}
              >
                <BadgeWithText badge={child.badge} text={child.text} />
              </Link>
            </Li>
          ))}
        </Ul>
      )}
    </Li>
  )
})
